/**
 * BLOCKS AN EMAIL ONLY EVER HAS ONE OF.
 *
 * Layne, 2026-08-25 (Teams, on the division feedback about duplicated modules):
 * "I see feedback around allowing duplication of modules. I feel like that needs to be
 * something set in the dashboard itself. like not allowed to duplicate the hero image, the
 * table header, the logo, etc." — and on the ECH standup the same morning she called
 * duplicate prevention "a dashboard-level fix, not a block fix". This is that fix.
 *
 * WHY IT HAS TO LIVE HERE AND NOT IN THE BLOCK. A Content Builder block cannot refuse to be
 * placed twice — it is markup. The only place that can say no is the surface that does the
 * placing, and there are exactly two of those: the ⧉ button on the block bar and `insertAt`
 * (the palette and the Inspector's Add-below). Both now ask this module.
 *
 * WHERE THE LIST CAME FROM — it is not a new opinion. `visualSpec` has carried this exact set
 * since the flat-column mapping was written, under the name `singleton`, to decide whether to
 * tick "Extra Blocks?" on the intake row: these are the types whose Airtable column holds ONE
 * instance, so a second one has nowhere to land and creative has to go read Block Order to
 * find out what really shipped. That made the duplicate a REPORTING problem. Layne is asking
 * for it to be a PLACEMENT problem instead, which is the right end of the pipe — a second
 * logo should never reach the row in the first place. `visualSpec` now imports this list
 * rather than keeping its own copy, so the two can never drift apart.
 *
 * ECH NAMES ARE INCLUDED ALONGSIDE THE HAND-BUILT TYPES. A doc holds repo types (`hero`,
 * `logo`) before an SFMC pull and ECH-registered types after one, and Layne's three examples
 * span both worlds — "the table header" only exists as `ECH_TableHeader`. So a block is
 * matched on its type AND on its ECH name, and either hit caps it.
 *
 * WHAT IS DELIBERATELY NOT HERE: every content module a division has a real reason to repeat
 * — copy blocks, images in a body, dividers, spacers, featured cards, CTAs. Capping those
 * would break real emails. When in doubt a type stays OUT: an over-broad cap blocks work
 * silently and gets reported as "the builder is broken", while a missing one is a row Katie
 * can still read.
 */
import { echMeta, echNameOf } from "./echRegistry";

/**
 * A HEADLINE BANNER IS RECOGNISED BY WHAT IT IS, NOT ONLY BY ITS NAME (2026-10-06).
 *
 * Layne added three templates on the `_TEST` set (We're Open, Last Chance, Floorplans) with
 * three new headline blocks — `ECH_WereOpenImage`, `ECH_LastChanceImage`,
 * `ECH_FloorplansImage`, copies of `ECH_ComingSoonImage` with new art — and expected no app
 * change. Three lists here and in the payload code named the banners one by one, so the new
 * ones were not capped at one per email and not treated as the hero on the Airtable row.
 *
 * Every banner carries `module: "heroImage"` in its own @GUARDRAILS (measured on the prod
 * ECH_ComingSoonImage, and copies inherit it), while `ECH_MainImage` is `mainImage`. So the
 * module is the test, and the names stay as the belt for an environment whose block carries
 * no guardrails. The next banner Layne adds needs no code change.
 */
export const ECH_HEADLINE_BANNERS: readonly string[] = [
  "ECH_ComingSoonImage", "ECH_AvailableNowImage", "ECH_JustLaunchedImage",
  "ECH_FindDreamHomeImage", "ECH_VIPImage",
  "ECH_WereOpenImage", "ECH_LastChanceImage", "ECH_FloorplansImage",
];
const BANNER_LC = new Set(ECH_HEADLINE_BANNERS.map((n) => n.toLowerCase()));

/** true when this block (by type, or by its SFMC name) is a headline banner */
export function isHeadlineBanner(type: string, name?: string): boolean {
  const t = String(type || "");
  const n = String(name || (t ? echNameOf(t) : "") || "");
  if (n && BANNER_LC.has(n.toLowerCase())) return true;
  const meta = t ? echMeta(t) : null;
  return !!meta && String((meta.gr && meta.gr.module) || "") === "heroImage";
}

/** repo block types that may appear at most once */
export const SINGLETON_TYPES: readonly string[] = [
  "hero", "image", "cta", "map", "livemap", "logo", "footer", "ftfooter", "listing",
];

/** SFMC block names that may appear at most once — Layne's "table header, the logo" */
export const SINGLETON_ECH_NAMES: readonly string[] = [
  "ECH_Logo", "ECH_MainImage", "ECH_TableHeader", "ECH_ListingTable", "ECH_SocialIcons",
  ...ECH_HEADLINE_BANNERS,
];

const ECH_LC = new Set(SINGLETON_ECH_NAMES.map((n) => n.toLowerCase()));

/** true when this block type is one the email may only hold once */
export function isSingletonType(type: string): boolean {
  const t = String(type || "");
  if (!t) return false;
  if (SINGLETON_TYPES.indexOf(t) > -1) return true;
  const n = echNameOf(t);
  if (!n) return false;
  return ECH_LC.has(n.toLowerCase()) || isHeadlineBanner(t, n);
}

/**
 * Would placing another `type` break the one-per-email rule?
 *
 * Counts what is ALREADY in the doc rather than trusting a flag, so it is correct after a
 * template load, a duplicated job, or an SFMC re-pull — none of which go through the two
 * placement surfaces and any of which can seed a doc that already holds one.
 */
export function wouldExceedSingleton(doc: { type: string }[], type: string): boolean {
  if (!isSingletonType(type)) return false;
  return (doc || []).some((b) => b && b.type === type);
}
